import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents, VariantSelectedEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;

    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      // Fetch and cache the content
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          // Cache the cloned element to avoid modifying the original
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }

    if (productGrid) {
      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
      this.#updateVariantPicker(productGrid);
    }

    this.#openQuickAddModal();
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    morph(modalContent, productGrid);

    // Wait for DOM updates
    await new Promise(resolve => requestAnimationFrame(resolve));

    // Force slideshow component to reinitialize after morph
    const slideshowComponent = modalContent.querySelector('slideshow-component');
    if (slideshowComponent && typeof slideshowComponent.updatedCallback === 'function') {
      slideshowComponent.updatedCallback();
    }

    // Manually trigger click events on thumbnail buttons to ensure they work
    // This is a workaround for the event delegation system not picking up morphed elements
    const thumbnailButtons = modalContent.querySelectorAll('slideshow-controls button[on\\:click^="/select"]');
    thumbnailButtons.forEach((button, index) => {
      // Remove any existing click listeners and add a direct one
      const newButton = button.cloneNode(true);
      button.parentNode?.replaceChild(newButton, button);

      newButton.addEventListener('click', (e) => {
        e.preventDefault();
        if (slideshowComponent && typeof slideshowComponent.select === 'function') {
          slideshowComponent.select(index, e);
        }
      });
    });

    // Initialize Amazon-style gallery if present
    const amazonGallery = modalContent.querySelector('.amazon-gallery');
    if (amazonGallery) {
      // Clear any existing gallery state
      if (amazonGallery._galleryCleanup) {
        amazonGallery._galleryCleanup();
      }

      // Wait for DOM to be ready
      setTimeout(() => {
        const thumbnails = amazonGallery.querySelectorAll('.amazon-thumbnail');
        const mediaItems = amazonGallery.querySelectorAll('.amazon-media-item');
        const prevBtn = amazonGallery.querySelector('#prevBtn');
        const nextBtn = amazonGallery.querySelector('#nextBtn');

        if (thumbnails.length === 0) return;

        let currentIndex = 0;
        let autoplayInterval = null;
        /** @type {Array<{element: Element, type: string, handler: EventListener}>} */
        const eventListeners = [];

        // Function to switch media
        const switchMedia = (index) => {
          currentIndex = index;
          thumbnails.forEach(t => t.classList.remove('active'));
          if (thumbnails[index]) thumbnails[index].classList.add('active');
          mediaItems.forEach((item, i) => {
            if (item instanceof HTMLElement) {
              item.style.display = i === index ? 'block' : 'none';
            }
          });
          // Pause all videos
          amazonGallery.querySelectorAll('video').forEach(v => v.pause());
          // Update navigation buttons
          if (prevBtn instanceof HTMLButtonElement) prevBtn.disabled = index === 0;
          if (nextBtn instanceof HTMLButtonElement) nextBtn.disabled = index === thumbnails.length - 1;
        };

        // Autoplay functions
        const startAutoplay = (delay) => {
          if (autoplayInterval) return;
          autoplayInterval = setInterval(() => {
            const nextIndex = (currentIndex + 1) % thumbnails.length;
            switchMedia(nextIndex);
          }, delay);
        };

        const stopAutoplay = () => {
          if (autoplayInterval) {
            clearInterval(autoplayInterval);
            autoplayInterval = null;
          }
        };

        // Bind thumbnail clicks
        thumbnails.forEach((thumb, index) => {
          const handler = () => switchMedia(index);
          thumb.addEventListener('click', handler);
          eventListeners.push({ element: thumb, type: 'click', handler });
        });

        // Ensure button content is set
        if (prevBtn instanceof HTMLButtonElement) {
          if (!prevBtn.textContent || prevBtn.textContent.trim() === '') {
            prevBtn.innerHTML = '‹';
          }
          const prevHandler = () => {
            if (currentIndex > 0) switchMedia(currentIndex - 1);
          };
          prevBtn.addEventListener('click', prevHandler);
          eventListeners.push({ element: prevBtn, type: 'click', handler: prevHandler });
        }
        if (nextBtn instanceof HTMLButtonElement) {
          if (!nextBtn.textContent || nextBtn.textContent.trim() === '') {
            nextBtn.innerHTML = '›';
          }
          const nextHandler = () => {
            if (currentIndex < thumbnails.length - 1) switchMedia(currentIndex + 1);
          };
          nextBtn.addEventListener('click', nextHandler);
          eventListeners.push({ element: nextBtn, type: 'click', handler: nextHandler });
        }

        // Pause on hover, resume on leave
        const mouseEnterHandler = stopAutoplay;
        const mouseLeaveHandler = () => startAutoplay(autoplayDelay);
        amazonGallery.addEventListener('mouseenter', mouseEnterHandler);
        amazonGallery.addEventListener('mouseleave', mouseLeaveHandler);
        eventListeners.push({ element: amazonGallery, type: 'mouseenter', handler: mouseEnterHandler });
        eventListeners.push({ element: amazonGallery, type: 'mouseleave', handler: mouseLeaveHandler });

        // Store cleanup function
        amazonGallery._galleryCleanup = () => {
          stopAutoplay();
          eventListeners.forEach(({ element, type, handler }) => {
            element.removeEventListener(type, handler);
          });
        };

        // Initialize first item
        switchMedia(0);

        // Start autoplay
        const autoplayDelay = 1000;
        startAutoplay(autoplayDelay);
      }, 100);
    }

    this.#syncVariantSelection(modalContent);
  }

  /**
   * Updates the quick-add button state based on whether a swatch is selected
   * @param {VariantSelectedEvent} event - The variant selected event
   */
  #updateQuickAddButtonState(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
